import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';
import CualpayHub from './CualpayHub';
import BcvReferencePrice from './BcvReferencePrice';
import VenezuelaEducationBlock from './VenezuelaEducationBlock';
import PagoMovilWaitlist from './PagoMovilWaitlist';

// Detecta si el usuario probablemente esta en Venezuela usando idioma del navegador + selector manual via localStorage.
const detectVenezuela = () => {
  try {
    const manual = localStorage.getItem('cualapp_country');
    if (manual === 've') return true;
    if (manual && manual !== 've') return false;
    const langs = (navigator.languages || [navigator.language || '']).map(l => l.toLowerCase());
    if (langs.some(l => l.includes('-ve') || l.endsWith('-ve'))) return true;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.toLowerCase().includes('caracas')) return true;
  } catch (_) { /* ignore */ }
  return false;
};

const CelulaComponent = () => {
  const [levels, setLevels] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [showIntention, setShowIntention] = useState(false);
  const [intention, setIntention] = useState('');
  const [volumeDesc, setVolumeDesc] = useState('');
  const [paymentPolling, setPaymentPolling] = useState(false);
  const [isVenezuela, setIsVenezuela] = useState(false);

  useEffect(() => {
    setIsVenezuela(detectVenezuela());
    fetchData();
    checkPaymentReturn();
  }, []);

  const toggleVenezuela = () => {
    const newVal = !isVenezuela;
    setIsVenezuela(newVal);
    try { localStorage.setItem('cualapp_country', newVal ? 've' : 'other'); } catch (_) {}
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [levelsRes, statusRes] = await Promise.all([
        axios.get(`${API}/celula/levels`),
        axios.get(`${API}/celula/status`)
      ]);
      setLevels(levelsRes.data.levels || []);
      setStatus(statusRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Verificar si regresamos de Stripe checkout
  const checkPaymentReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const paymentStatus = params.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      setPaymentPolling(true);
      // Limpiar URL
      window.history.replaceState({}, '', window.location.pathname);
      // Poll payment status
      await pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
      alert('Pago cancelado. Puedes intentar de nuevo.');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 8;
    if (attempts >= maxAttempts) {
      setPaymentPolling(false);
      alert('No se pudo verificar el pago. Intenta recargar la pagina.');
      return;
    }
    try {
      const res = await axios.get(`${API}/celula/payment/status/${sessionId}`);
      if (res.data.status === 'paid') {
        setPaymentPolling(false);
        alert(`Celula activada exitosamente! Nivel: ${res.data.celula_level}`);
        fetchData();
        return;
      } else if (res.data.status === 'expired') {
        setPaymentPolling(false);
        alert('Sesion de pago expirada.');
        return;
      }
      // Still processing, retry
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handlePurchase = async (levelId) => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/celula/checkout`, {
        level: levelId,
        origin_url: window.location.origin
      });
      // Redirigir a Stripe Checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) { alert(err.response?.data?.detail || 'Error al iniciar pago'); setPurchasing(false); }
  };

  const handleUpgrade = async (levelId) => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/celula/upgrade/checkout`, {
        level: levelId,
        origin_url: window.location.origin
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) { alert(err.response?.data?.detail || 'Error al iniciar pago'); setPurchasing(false); }
  };

  const handleDeclareIntention = async () => {
    if (!intention) return alert('Selecciona una intencion');
    try {
      const res = await axios.post(`${API}/celula/wen/declare-intention`, {
        intention,
        volume_description: volumeDesc
      });
      alert(res.data.message);
      setShowIntention(false);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const levelColors = {
    semilla: { bg: 'from-emerald-50 to-emerald-100', border: 'border-emerald-300', text: 'text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-700' },
    basico: { bg: 'from-gray-100 to-gray-200', border: 'border-gray-300', text: 'text-gray-700', btn: 'bg-gray-600 hover:bg-gray-700' },
    medio: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-300', text: 'text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700' },
    estandar: { bg: 'from-blue-50 to-blue-100', border: 'border-blue-300', text: 'text-blue-700', btn: 'bg-blue-600 hover:bg-blue-700' },
    premium: { bg: 'from-purple-50 to-purple-100', border: 'border-purple-300', text: 'text-purple-700', btn: 'bg-purple-600 hover:bg-purple-700' },
    consolidado: { bg: 'from-amber-50 to-amber-100', border: 'border-amber-300', text: 'text-amber-700', btn: 'bg-amber-600 hover:bg-amber-700' },
    empresarial: { bg: 'from-amber-50 to-amber-100', border: 'border-amber-300', text: 'text-amber-700', btn: 'bg-amber-600 hover:bg-amber-700' }
  };

  const levelOrder = ['semilla', 'basico', 'estandar', 'premium', 'empresarial'];

  if (loading) return <div className="text-center py-12 text-gray-500">Cargando...</div>;

  if (paymentPolling) return (
    <div className="text-center py-12" data-testid="payment-polling">
      <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
      <p className="text-lg font-semibold text-gray-700">Verificando tu pago...</p>
      <p className="text-sm text-gray-500 mt-2">Esto tomara unos segundos</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="celula-component">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Mi Celula</h2>
        <p className="text-gray-600">Tu acceso al ecosistema CUALAPP</p>
        <p className="text-xs text-gray-400 mt-1">CUALAPP opera como sistema de coordinacion economica. No custodia fondos.</p>
      </div>

      {/* Current Status */}
      {status?.has_celula && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-6" data-testid="celula-status">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-emerald-800">Celula Activa</h3>
              <p className="text-emerald-600 text-sm">Nivel: <span className="font-bold">{status.celula.level_name}</span></p>
              <p className="text-emerald-600 text-sm">Capacidad WEN: <span className="font-bold">{status.celula.wen_capacity}</span></p>
              <p className="text-emerald-600 text-sm">Dominios: hasta {status.celula.max_domains_active}</p>
            </div>
            <div className="text-4xl">🔑</div>
          </div>

          {/* WEN Profile */}
          {status.wen && (
            <div className="mt-4 pt-4 border-t border-emerald-200">
              <h4 className="font-semibold text-emerald-700 text-sm">Perfil WEN (Espacio de Gestion)</h4>
              {status.wen.economic_intention ? (
                <div className="mt-2 text-sm text-emerald-600">
                  <p>Intencion economica: <span className="font-bold">{status.wen.economic_intention}</span></p>
                  {status.wen.declared_volume && <p>Volumen: {status.wen.declared_volume}</p>}
                </div>
              ) : (
                <button
                  onClick={() => setShowIntention(true)}
                  data-testid="declare-intention-btn"
                  className="mt-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
                >
                  Declarar intencion economica
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Intention Declaration Modal */}
      {showIntention && (
        <div className="bg-white rounded-xl shadow-lg border border-blue-200 p-6" data-testid="intention-form">
          <h3 className="font-bold text-gray-900 mb-4">Declarar Intencion Economica</h3>
          <p className="text-sm text-gray-600 mb-4">
            CUALAPP usara esta informacion para modelar y optimizar tu experiencia economica.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Carga de trabajo deseada:
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { val: 'baja', label: 'Baja', desc: 'Uso personal basico' },
                  { val: 'media', label: 'Media', desc: 'Gestion de nicho productivo' },
                  { val: 'alta', label: 'Alta', desc: 'Multi-negocio o empresa' }
                ].map(opt => (
                  <button
                    key={opt.val}
                    onClick={() => setIntention(opt.val)}
                    data-testid={`intention-${opt.val}`}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      intention === opt.val
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <p className="font-semibold text-sm">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Describe brevemente tu actividad economica (opcional):
              </label>
              <textarea
                value={volumeDesc}
                onChange={(e) => setVolumeDesc(e.target.value)}
                data-testid="volume-description"
                className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none"
                rows={3}
                placeholder="Ej: Tengo un negocio de comida y quiero gestionar proveedores e inventario..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDeclareIntention}
                data-testid="submit-intention"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Confirmar
              </button>
              <button
                onClick={() => setShowIntention(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Level Cards */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          {status?.has_celula ? 'Mejorar nivel' : 'Selecciona tu Celula'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {levels.map((level) => {
            const colors = levelColors[level.id] || levelColors.basico;
            const currentLevelIdx = status?.has_celula ? levelOrder.indexOf(status.celula.level) : -1;
            const thisLevelIdx = levelOrder.indexOf(level.id);
            const isCurrent = status?.has_celula && status.celula.level === level.id;
            const canUpgrade = status?.has_celula && thisLevelIdx > currentLevelIdx;
            const isLower = status?.has_celula && thisLevelIdx <= currentLevelIdx && !isCurrent;

            return (
              <div
                key={level.id}
                onClick={() => !status?.has_celula && setSelectedLevel(level.id)}
                data-testid={`celula-level-${level.id}`}
                className={`relative bg-gradient-to-br ${colors.bg} rounded-xl border-2 ${
                  isCurrent ? 'border-emerald-400 ring-2 ring-emerald-200' :
                  selectedLevel === level.id ? 'border-blue-400 ring-2 ring-blue-200' :
                  colors.border
                } p-5 ${!status?.has_celula ? 'cursor-pointer hover:shadow-md' : ''} transition-all ${isLower ? 'opacity-50' : ''}`}
              >
                {isCurrent && (
                  <div className="absolute -top-2 -right-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full">
                    Actual
                  </div>
                )}
                <div className="flex justify-between items-start mb-3">
                  <h4 className={`text-lg font-bold ${colors.text}`}>{level.name}</h4>
                  <div className="text-right">
                    <span className={`text-2xl font-bold ${colors.text}`}>${level.price_usd}</span>
                    {isVenezuela && <BcvReferencePrice usdAmount={level.price_usd} />}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">{level.description}</p>
                <div className="space-y-1 mb-4">
                  {level.features.map((f, i) => (
                    <p key={i} className="text-xs text-gray-500 flex items-center gap-1">
                      <span className="text-emerald-500">&#10003;</span> {f}
                    </p>
                  ))}
                </div>
                <div className="text-xs text-gray-500 mb-3">
                  <span>WEN: {level.wen_capacity}</span>
                  <span className="mx-2">|</span>
                  <span>Dominios: {level.max_domains_active}</span>
                  <span className="mx-2">|</span>
                  <span>Items: {level.max_items_per_domain === -1 ? 'ilimitado' : level.max_items_per_domain}/dominio</span>
                </div>

                {!status?.has_celula ? (
                  <div className="w-full py-2 text-center text-xs text-purple-700 italic bg-purple-50 rounded">
                    Elige metodo de pago abajo {'\u2193'}
                  </div>
                ) : canUpgrade ? (
                  <button
                    onClick={() => handleUpgrade(level.id)}
                    disabled={purchasing}
                    data-testid={`upgrade-${level.id}`}
                    className={`w-full py-2 text-white rounded-lg text-sm font-medium ${colors.btn} disabled:opacity-50`}
                  >
                    {purchasing ? 'Redirigiendo a Stripe...' : `Mejorar a ${level.name}`}
                  </button>
                ) : isCurrent ? (
                  <div className="w-full py-2 text-center text-emerald-600 text-sm font-medium">
                    Tu nivel actual
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selector de pais informal (Venezuela activa BCV referencial + bloque educativo) */}
      {!status?.has_celula && (
        <div className="text-center -mt-2">
          <button
            onClick={toggleVenezuela}
            data-testid="toggle-venezuela"
            className="text-[11px] text-gray-500 underline hover:text-emerald-700"
          >
            {isVenezuela
              ? 'Estas viendo precios con referencia Bs. (Venezuela). Cambiar'
              : 'Estoy en Venezuela: ver precio referencial en Bs.'}
          </button>
        </div>
      )}

      {/* CUALpay - Sistema de pagos unificado */}
      {!status?.has_celula && levels.length > 0 && (
        <CualpayHub
          levels={levels}
          isVenezuela={isVenezuela}
          purchasing={purchasing}
          onStripeCheckout={(levelId) => handlePurchase(levelId)}
          onSuccess={() => fetchData()}
        />
      )}

      {/* Bloques especificos para Venezuela */}
      {!status?.has_celula && isVenezuela && (
        <div className="space-y-4" data-testid="venezuela-extras">
          <VenezuelaEducationBlock />
          <PagoMovilWaitlist />
        </div>
      )}

      {/* Legal Notice */}
      <div className="text-center text-xs text-gray-400 mt-4 p-4 bg-gray-50 rounded-lg">
        <p>CUALAPP opera como un sistema de coordinacion economica digital.</p>
        <p>La plataforma no custodia fondos directamente. Pagos procesados por Stripe (tarjeta), red TRON (USDT) o Zelle (verificacion manual).</p>
        <p>El usuario mantiene control sobre sus recursos en todo momento.</p>
      </div>
    </div>
  );
};




export default CelulaComponent;
